import { h, render, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';

interface WidgetConfig {
  primary_color: string;
  persona_name: string;
  position: string;
  theme: string;
  tooltip_message: string | null;
  greeting: string | null;
  size: string;
  widget_active: boolean;
  hide_on_mobile: boolean;
  offset_bottom: number;
  offset_x: number;
  offset_mobile_y: number;
  offset_mobile_x: number;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// chat state machine
type ChatState = 'normal' | 'awaiting_confirmation' | 'awaiting_email' | 'done';

interface WidgetProps {
  orgId: string;
  userRole?: string;
  apiBase?: string;
  token?: string;
}

const API_BASE = 'https://www.plotui.com';

export function Widget({ orgId, userRole = '', apiBase = API_BASE, token }: WidgetProps) {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(() =>
    typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('plotui_tooltip_dismissed')
  );
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatState, setChatState] = useState<ChatState>('normal');
  const [pendingQuestion, setPendingQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${apiBase}/api/widget/config?org_id=${orgId}`)
      .then(r => r.json())
      .then((cfg: WidgetConfig) => setConfig(cfg))
      .catch(() => {});
  }, [orgId, apiBase]);

  // Mobile detection — combines viewport width AND UA so WebViews without
  // <meta name="viewport"> (which default to 980px layout width) are caught.
  useEffect(() => {
    const check = () => {
      const byWidth = window.innerWidth < 768;
      const byUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent);
      setIsMobile(byWidth || byUA);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatState]);

  const sessionId = (() => {
    let id = localStorage.getItem('plotui_session_id');
    if (!id) {
      id = `anon_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('plotui_session_id', id);
    }
    return id;
  })();

  const brandColor = config?.primary_color || '#6366f1';
  const personaName = config?.persona_name || 'Support';
  const isDark = (config?.theme || 'dark') === 'dark';
  const isRight = (config?.position || 'bottom-right') === 'bottom-right';

  // Position styles — safe-area-inset-bottom handles iOS home bar + Android nav bar uniformly
  const offsetBottom = config?.offset_bottom ?? 0;
  const offsetX = config?.offset_x ?? 0;
  const offsetMobileY = config?.offset_mobile_y ?? 0;
  const offsetMobileX = config?.offset_mobile_x ?? 0;

  const btnPositionStyle = {
    position: 'fixed' as const,
    bottom: `calc(1.5rem + env(safe-area-inset-bottom) + ${offsetMobileY}px)`,
    [isRight ? 'right' : 'left']: `calc(1.5rem + ${offsetMobileX}px)`,
    zIndex: 2147483647,
  };
  const panelPositionStyle = {
    position: 'fixed' as const,
    bottom: `calc(5.5rem + env(safe-area-inset-bottom) + ${offsetMobileY}px)`,
    [isRight ? 'right' : 'left']: `calc(1rem + ${offsetMobileX}px)`,
    zIndex: 2147483647,
  };

  // Inject responsive overrides via a <style> tag so we can use media queries
  useEffect(() => {
    const styleId = 'plotui-widget-offsets';
    let el = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }
    el.textContent = `
      @media (min-width: 768px) {
        .plotui-btn  { bottom: calc(1.5rem + ${offsetBottom}px) !important; ${isRight ? 'right' : 'left'}: calc(1.5rem + ${offsetX}px) !important; }
        .plotui-panel { bottom: calc(5.5rem + ${offsetBottom}px) !important; ${isRight ? 'right' : 'left'}: calc(1rem + ${offsetX}px) !important; }
      }
    `;
  }, [offsetBottom, offsetX, offsetMobileY, offsetMobileX, isRight]);

  const bg = isDark ? '#111111' : '#ffffff';
  const border = isDark ? '#2a2a2a' : '#e5e7eb';
  const msgBg = isDark ? '#1f1f1f' : '#f3f4f6';
  const textColor = isDark ? '#ffffff' : '#111111';
  const subText = isDark ? '#9ca3af' : '#6b7280';

  const sendMessage = async () => {
    if (!message.trim() || isLoading) return;
    const userMessage = message.trim();
    setMessage('');
    const newMessages: Message[] = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const history = newMessages.slice(-7, -1); // last 3 prior exchanges (6 msgs)
      const body: Record<string, unknown> = {
        org_id: orgId,
        message: userMessage,
        page_url: window.location.href,
        user_role: userRole,
        session_id: sessionId,
        conversation_history: history,
      };
      if (token) body.token = token;

      const response = await fetch(`${apiBase}/api/widget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);

      if (data.show_contact_link) {
        setPendingQuestion(userMessage);
        setChatState('awaiting_confirmation');
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmContact = () => {
    setChatState('awaiting_email');
  };

  const declineContact = () => {
    setChatState('normal');
  };

  const submitEmail = async () => {
    if (!email.trim()) return;
    setIsLoading(true);
    try {
      const body: Record<string, unknown> = {
        org_id: orgId,
        message: pendingQuestion,
        page_url: window.location.href,
        user_role: userRole,
        session_id: sessionId,
        user_email: email.trim(),
        flag_unresolved: true,
      };
      if (token) body.token = token;

      await fetch(`${apiBase}/api/widget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Got it! We'll email you at ${email.trim()} once the team has an answer.`,
      }]);
      setChatState('done');
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong submitting your email. Please try again.' }]);
      setChatState('normal');
    } finally {
      setIsLoading(false);
    }
  };

  const showTooltip = !isOpen && !tooltipDismissed && !!config?.tooltip_message;

  function dismissTooltip() {
    sessionStorage.setItem('plotui_tooltip_dismissed', '1');
    setTooltipDismissed(true);
  }

  // Don't render until config is loaded — prevents flashing default purple color
  if (!config) return null;
  if (!config.widget_active) return null;
  if (config.hide_on_mobile && isMobile) return null;

  return (
    <>
      <style>{`
        .plotui-btn { transition: transform 0.15s ease; }
        .plotui-btn:hover { transform: scale(1.1); }
      `}</style>

      {/* Tooltip — sits above the button with a downward pointer arrow */}
      {showTooltip && (
        <div style={{
          position: 'fixed',
          bottom: `calc(1.5rem + env(safe-area-inset-bottom) + ${offsetMobileY}px + 3.75rem + 0.5rem)`,
          [isRight ? 'right' : 'left']: `calc(1.25rem + ${offsetMobileX}px)`,
          zIndex: 2147483646,
          cursor: 'pointer',
        }} onClick={() => { dismissTooltip(); setIsOpen(true); }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            backgroundColor: '#1f2937',
            color: '#ffffff',
            padding: '0.4rem 0.75rem',
            borderRadius: '0.5rem',
            fontSize: '0.8rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100vw - 6rem)',
          }}>
            <span>{config.tooltip_message}</span>
            <button
              onClick={(e) => { e.stopPropagation(); dismissTooltip(); }}
              style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '0 0.1rem', fontSize: '0.9rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}
            >
              ×
            </button>
          </div>
          {/* downward-pointing arrow */}
          <div style={{
            width: 0, height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid #1f2937',
            marginLeft: 'auto',
            marginRight: `calc(0.75rem + ${offsetMobileX}px)`,
          }} />
        </div>
      )}

      {!isOpen && (
        <button
          class="plotui-btn"
          onClick={() => setIsOpen(true)}
          style={{ ...btnPositionStyle, backgroundColor: brandColor, height: '3.5rem', width: '3.5rem', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', cursor: 'pointer', border: 'none' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z"/>
          </svg>
        </button>
      )}

      {isOpen && (
        <div
          class="plotui-panel"
          style={{ ...panelPositionStyle,
            width: config.size === 'compact'
              ? 'clamp(260px, calc(100vw - 1.5rem), 310px)'
              : 'clamp(300px, calc(100vw - 1.5rem), 376px)',
            height: config.size === 'compact'
              ? 'clamp(340px, 62vh, 440px)'
              : 'clamp(420px, 75vh, 560px)', borderRadius: '0.75rem', border: `1px solid ${border}`, backgroundColor: bg, boxShadow: '0 25px 50px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column' }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${border}`, padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ height: '2.5rem', width: '2.5rem', borderRadius: '9999px', backgroundColor: brandColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
                {personaName.charAt(0)}
              </div>
              <div>
                <p style={{ fontWeight: 600, color: textColor, margin: 0 }}>{personaName}</p>
                <p style={{ fontSize: '0.75rem', color: subText, margin: 0 }}>Online</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: subText, padding: '0.25rem' }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <p style={{ color: textColor, margin: 0 }}>{config?.greeting || 'Hi! I can help you navigate this application.'}</p>
                <p style={{ fontSize: '0.875rem', color: subText, marginTop: '0.5rem' }}>What do you need?</p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '80%', borderRadius: '0.75rem', padding: '0.5rem 0.875rem',
                  backgroundColor: msg.role === 'user' ? brandColor : msgBg,
                  color: msg.role === 'user' ? '#fff' : textColor,
                  fontSize: '0.875rem', lineHeight: '1.5',
                }}>
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Confirmation buttons — shown after show_contact_link: true */}
            {chatState === 'awaiting_confirmation' && !isLoading && (
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  onClick={confirmContact}
                  style={{ backgroundColor: brandColor, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Yes, notify me
                </button>
                <button
                  onClick={declineContact}
                  style={{ backgroundColor: 'transparent', color: subText, border: `1px solid ${border}`, borderRadius: '0.5rem', padding: '0.4rem 0.875rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  No thanks
                </button>
              </div>
            )}

            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ backgroundColor: msgBg, borderRadius: '0.75rem', padding: '0.5rem 0.875rem', display: 'flex', gap: '0.25rem' }}>
                  {[0, 0.1, 0.2].map(delay => (
                    <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '9999px', backgroundColor: subText, animation: `plotui-bounce 1s ${delay}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div style={{ borderTop: `1px solid ${border}`, padding: '0.75rem' }}>
            {chatState === 'awaiting_email' ? (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="email"
                  value={email}
                  onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                  onKeyPress={(e) => e.key === 'Enter' && submitEmail()}
                  placeholder="your@email.com"
                  autoFocus
                  style={{ flex: 1, borderRadius: '0.5rem', border: `1px solid ${border}`, backgroundColor: msgBg, padding: '0.5rem 0.875rem', color: textColor, fontSize: '0.875rem', outline: 'none' }}
                />
                <button
                  onClick={submitEmail}
                  disabled={!email.trim() || isLoading}
                  style={{ backgroundColor: brandColor, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', opacity: (!email.trim() || isLoading) ? 0.5 : 1 }}
                >
                  Send
                </button>
              </div>
            ) : chatState === 'done' ? (
              <p style={{ textAlign: 'center', fontSize: '0.75rem', color: subText, margin: 0 }}>We'll be in touch via email.</p>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={message}
                  onInput={(e) => setMessage((e.target as HTMLInputElement).value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your question..."
                  style={{ flex: 1, borderRadius: '0.5rem', border: `1px solid ${border}`, backgroundColor: msgBg, padding: '0.5rem 0.875rem', color: textColor, fontSize: '0.875rem', outline: 'none' }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!message.trim() || isLoading}
                  style={{ backgroundColor: brandColor, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', opacity: (!message.trim() || isLoading) ? 0.5 : 1 }}
                >
                  Send
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes plotui-bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </>
  );
}

// Initialize widget
function initWidget() {
  const script = document.querySelector('script[data-org]') as HTMLScriptElement | null;
  if (!script) return;

  const orgId = script.getAttribute('data-org');
  const userRole = script.getAttribute('data-role') || '';
  const apiBase = script.getAttribute('data-api') || API_BASE;
  const token = script.getAttribute('data-token') || undefined;

  if (!orgId) {
    console.error('PlotUI Widget: Missing data-org attribute');
    return;
  }

  const container = document.createElement('div');
  document.body.appendChild(container);

  render(
    <Widget
      orgId={orgId}
      userRole={userRole}
      apiBase={apiBase}
      token={token}
    />,
    container
  );
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWidget);
} else {
  initWidget();
}
